import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const fmt = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);
// EP El Paisa · TP Tecnoplaza · MT Metro · IL IluminaLEDs
const PREFIJO_SKU = { 'El Paisa':'EP', Tecnoplaza:'TP', Metro:'MT', IluminaLEDs:'IL' };
const PROV_COLOR = { Tecnoplaza:'#3B82F6', Metro:'#EC4899', IluminaLEDs:'#FDE047', 'El Paisa':'#F97316' };

export default function Catalogo() {
  const { usuario } = useAuth();
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [total, setTotal] = useState(0);
  const [busqueda, setBusq] = useState('');
  const [catFiltro, setCatFiltro] = useState('');
  const [provFiltro, setProvFiltro] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(false);
  const [modalImport, setModalImport] = useState(false);
  const [proveedorImport, setProveedorImport] = useState('Tecnoplaza');
  const [editando, setEditando] = useState(null);
  const [textoPDF, setTextoPDF] = useState('');
  const [importando, setImportando] = useState(false);
  const [msgImport, setMsgImport] = useState('');
  const [form, setForm] = useState({ sku:'', nombre:'', categoria:'', precio_costo:'', precio_venta:'', precio_antes:'', proveedor:'Tecnoplaza' });
  const [msg, setMsg] = useState('');
  const LIMIT = 50;

  const esAdmin = ['admin','superadmin'].includes(usuario?.rol);

  const cargar = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit:LIMIT });
      if (busqueda) params.append('q', busqueda);
      if (catFiltro) params.append('categoria', catFiltro);
      if (provFiltro) params.append('proveedor', provFiltro);
      const r = await api.get(`/api/catalogo?${params}`);
      setProductos(r.data.productos);
      setTotal(r.data.total);
      setCategorias(r.data.categorias);
      setProveedores(r.data.proveedores || []);
    } catch(e) { console.error(e); }
  }, [busqueda, catFiltro, provFiltro, page]);

  useEffect(() => { cargar(); }, [cargar]);

  /**
   * El precio sugerido lo calcula el SERVIDOR, no esta pantalla.
   *
   * Aquí vivía una copia de la escala de márgenes. Con El Paisa dejó de servir —su escala
   * arranca en +100% y esta copia decía +35%— y el precio que se veía antes de guardar no
   * era el que se guardaba. La regla de plata ahora está en un solo archivo y aquí se
   * pregunta. `sugerido` guarda también el porcentaje para poder mostrarlo.
   */
  const [sugerido, setSugerido] = useState(null);
  const [precioTocado, setPrecioTocado] = useState(false);

  useEffect(() => {
    if (!modal) return;
    const costo = +form.precio_costo;
    if (!costo) { setSugerido(null); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/api/catalogo/precio-sugerido', {
          params: { proveedor: form.proveedor, costo },
        });
        if (!vivo) return;
        setSugerido(data);
        // Si Víctor ya escribió un precio a mano, no se le pisa.
        if (!precioTocado) setForm(p => ({ ...p, precio_venta: data.precio_venta }));
      } catch (e) { /* si falla, queda el precio que haya escrito */ }
    }, 350);
    return () => { vivo = false; clearTimeout(t); };
  }, [modal, form.precio_costo, form.proveedor, precioTocado]);

  /**
   * La foto, en el mismo paso que el producto.
   *
   * La tienda no publica nada sin foto —es una regla que ya existía— así que dejar la foto
   * para un segundo paso es crear productos invisibles que nadie sabe por qué no aparecen.
   * Se encoge a 420px como el botón 📷 de la lista: la misma medida, para que una foto
   * puesta aquí y otra puesta allá pesen igual en la base.
   */
  const escogerFoto = () => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*';
    inp.multiple = true;      // el proveedor manda varias del mismo producto
    inp.onchange = async () => {
      const files = [...(inp.files || [])];
      if (!files.length) return;
      const extras = [];
      for (let i = 0; i < files.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        const b = await encogerFoto(files[i]);
        if (!b) continue;
        if (i === 0) f('imagen_base64', b); else extras.push(b);
      }
      // Las de más van a la galería de la ficha; la primera es la de la tarjeta de la tienda.
      f('fotos', extras);
      /**
       * La grande, solo de la primera y solo para LEERLA. A 420px la letra chica del aviso
       * queda ilegible —el nombre grande se lee, las características no— y la lectura volvía
       * sin especificaciones. Esta copia no se guarda en la base.
       */
      // eslint-disable-next-line no-await-in-loop
      const grande = await encogerFoto(files[0], 1100, 0.85);
      setFotoGrande(grande ? 'data:image/jpeg;base64,' + grande : null);
      setLectura(null);
    };
    inp.click();
  };

  const [fotoGrande, setFotoGrande] = useState(null);
  const [lectura, setLectura] = useState(null);
  const [leyendo, setLeyendo] = useState(false);

  /**
   * Leer el aviso del proveedor y proponer el producto.
   *
   * Lo que vuelve se ESCRIBE EN EL FORMULARIO para que Víctor lo revise, no se guarda. Un
   * modelo leyendo una imagen se equivoca —confunde un modelo con otro, lee «2 litros»
   * donde dice «2 velocidades»— y publicar sin que nadie mire sería poner a vender datos
   * que nadie comprobó. El precio no se toca: no está en la imagen.
   */
  const leerLaFoto = async () => {
    if (!fotoGrande) return setMsg('Escoge primero la foto que te mandó el proveedor');
    setLeyendo(true); setMsg('');
    try {
      const { data } = await api.post('/api/catalogo/leer-foto', { imagen: fotoGrande });
      if (!data.ok) { setMsg(data.porque || 'No se pudo leer la foto'); setLectura(null); }
      else {
        setLectura(data);
        const ficha = [data.descripcion, ...(data.caracteristicas || []).map(c => '· ' + c)]
          .filter(Boolean).join('\n');
        setForm(p => ({
          ...p,
          nombre: data.nombre || p.nombre,
          descripcion: ficha || p.descripcion,
          subcategoria: (p.proveedor === 'El Paisa' && data.categoria) ? data.categoria : p.subcategoria,
        }));
      }
    } catch (e) { setMsg(e.response?.data?.porque || 'No se pudo leer la foto'); }
    setLeyendo(false);
  };

  /* ══ SUBIR A LA TIENDA LO QUE EL PROVEEDOR OFRECE POR WHATSAPP ══
   *
   * Casi todos los días llega por WhatsApp una imagen con uno o varios productos y su precio
   * de oferta. Unos ya están en el catálogo y otros no. Antes eso eran: abrir Catálogo,
   * decidir si ya existía, y escribir a mano nombre, descripción, categoría, precio y código
   * — por cada producto, todos los días.
   *
   * Aquí se sube la imagen una vez y salen fichas para revisar: se corrige lo que haga falta,
   * se dice si es nuevo o cuál de los que ya tiene es, y se publica. Nada se guarda sin que
   * Víctor lo confirme — el precio menos que nada.
   */
  const [modalAviso, setModalAviso] = useState(false);
  const [avisoTexto, setAvisoTexto] = useState('');
  /**
   * Varias fotos del mismo producto: El Paisa manda cuatro del mismo reloj, y con una sola el
   * cliente no ve el detalle que lo decide. La PRIMERA es la principal —la que sale en la
   * tarjeta de la tienda— y las demás van a la galería de la ficha.
   */
  const [fotos, setFotos] = useState([]);                // base64 sin el encabezado
  const [avisoFotoGrande, setAvisoFotoGrande] = useState(null);  // la que se manda a leer
  const [avisoLeyendo, setAvisoLeyendo] = useState(false);
  const [avisoMsg, setAvisoMsg] = useState('');
  const [avisoProveedor, setAvisoProveedor] = useState('El Paisa');
  const [ficha, setFicha] = useState(null);
  // Nombre propio: `sugerido` ya existe arriba, y es el del modal de «+ Nuevo producto».
  const [sugeridoAviso, setSugeridoAviso] = useState(null);

  // Los departamentos de verdad, para escogerlos en vez de adivinarlos.
  const [deps, setDeps] = useState([]);

  const abrirAviso = async () => {
    setAvisoTexto(''); setFotos([]); setAvisoFotoGrande(null);
    setFicha(null); setSugeridoAviso(null); setAvisoMsg('');
    setModalAviso(true);
    try {
      const { data } = await api.get('/api/catalogo/categorias-de');
      setDeps(data || []);
    } catch (e) { setDeps([]); }
  };

  /** La misma idea que en «+ Nuevo producto»: una copia chica para guardar y una grande para
   *  leer. A 420px la letra chica del aviso es ilegible y la lectura vuelve sin datos. */
  const escogerFotoAviso = () => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*';
    inp.multiple = true;    // el proveedor manda varias del mismo producto
    inp.onchange = async () => {
      const files = [...(inp.files || [])];
      if (!files.length) return;
      setAvisoMsg('');
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((listo) => {
          const img = new Image();
          img.onload = () => {
            const encoger = (max, calidad) => {
              const c = document.createElement('canvas');
              const escala = Math.min(1, max / Math.max(img.width, img.height));
              c.width = Math.round(img.width * escala); c.height = Math.round(img.height * escala);
              c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
              return c.toDataURL('image/jpeg', calidad);
            };
            setFotos(xs => (xs.length >= 8 ? xs : [...xs, encoger(420, 0.82).split(',')[1]]));
            // La grande solo de la primera: es la que se manda a leer, y a 420px la letra
            // chica del aviso es ilegible.
            setAvisoFotoGrande(g => g || encoger(1100, 0.85));
            listo();
          };
          img.onerror = listo;
          img.src = URL.createObjectURL(file);
        });
      }
    };
    inp.click();
  };

  const quitarFoto = (i) => setFotos(xs => xs.filter((_, j) => j !== i));

  /**
   * Escoger cuál foto es la principal.
   *
   * La primera del aviso es la publicidad del mayorista: lleva su logo, letras encima y
   * precios. Las otras son fotos limpias del producto — justo las que sirven para la tienda.
   * Borrarle el logo a una imagen es difícil y sale mal; escoger otra es un toque.
   */
  const hacerPrincipal = (i) => setFotos(xs => (i === 0 ? xs : [xs[i], ...xs.filter((_, j) => j !== i)]));

  const cambiar = (campos) => setFicha(x => ({ ...x, ...campos }));

  /**
   * Se lee el mensaje. El texto manda: «Precio Mayorista:$14.500» es una lectura exacta,
   * mientras que el mismo número sacado de la foto siempre es una interpretación. La imagen
   * solo se usa si el texto no trajo nombre — el caso de Metro, cuyo mensaje es solo el precio.
   */
  const leerAviso = async () => {
    if (!avisoTexto.trim() && !avisoFotoGrande) {
      return setAvisoMsg('Pega el mensaje que te mandaron, o escoge las imágenes');
    }
    setAvisoLeyendo(true); setAvisoMsg('');
    try {
      // El proveedor viaja porque cambia cómo se lee: en los avisos de Metro el número de la
      // imagen es su código de catálogo, no el precio.
      const { data } = await api.post('/api/catalogo/aviso/leer', {
        texto: avisoTexto, imagen: avisoFotoGrande || undefined, proveedor: avisoProveedor });
      setFicha({ ...data, elegido: null, candidatos: null, estado: 'pendiente', msg: '',
                 precio_venta: '', departamento: '', subcategoria_elegida: '' });
      if (!data.nombre && !data.precio_costo) {
        setAvisoMsg('No se reconoció ni el nombre ni el precio. Escríbelos tú abajo.');
      }
      buscarParecidos(data);
      pedirSugerido(data.precio_costo);
    } catch (e) { setAvisoMsg(e.response?.data?.error || 'No se pudo leer el mensaje'); }
    setAvisoLeyendo(false);
  };

  /** El precio que da TU escala de márgenes. Se pregunta, no se calcula aquí: la escala vive
   *  en el servidor y una copia en la pantalla acabaría diciendo otro número. */
  const pedirSugerido = async (costo) => {
    if (!costo) return setSugeridoAviso(null);
    try {
      const { data } = await api.get('/api/catalogo/precio-sugerido',
        { params: { proveedor: avisoProveedor, costo } });
      setSugeridoAviso(data);
    } catch (e) { setSugeridoAviso(null); }
  };

  /** Se PROPONEN candidatos; escoger es de Víctor. Emparejar solo por parecido de texto ya
   *  emparejó un teclado de $900.000 con uno de $70.000 en Comparar proveedores. */
  const buscarParecidos = async (base) => {
    const p = base || ficha;
    if (!p?.nombre && !p?.codigo_proveedor) return setFicha(x => ({ ...x, candidatos: [] }));
    try {
      const { data } = await api.post('/api/catalogo/aviso/parecidos', {
        nombre: p.nombre, marca: p.marca, referencia: p.referencia,
        // El código identifica sin lugar a duda; va con el proveedor porque los de Metro son
        // internos suyos y no significan nada en el catálogo de otro.
        codigo: p.codigo_proveedor, proveedor: avisoProveedor });
      const cands = data.candidatos || [];
      /**
       * Si uno vino por CÓDIGO, se escoge solo. No es un parecido que haya que valorar: es el
       * mismo producto. Hacerle tocar un botón para confirmar lo que ya es seguro es trabajo
       * de mas — y con 2.366 productos, es justo lo que no puede recordar.
       */
      const exacto = cands.find(c => c.por_codigo);
      setFicha(x => ({ ...x, candidatos: cands, elegido: exacto ? exacto.id : x.elegido }));
    } catch (e) { setFicha(x => ({ ...x, candidatos: [] })); }
  };

  const publicarAviso = async () => {
    const p = ficha;
    cambiar({ estado: 'subiendo', msg: '' });
    try {
      const texto = [p.descripcion, ...(p.caracteristicas || []).map(c => '· ' + c)]
        .filter(Boolean).join('\n');
      const { data } = await api.post('/api/catalogo/aviso/publicar', {
        producto_id: p.elegido || undefined,
        nombre: p.nombre,
        descripcion: texto || undefined,
        // Lo que Víctor escogió manda. Adivinar por el nombre no sirve con estos productos:
        // «Lampara de uñas» caía en Iluminación y un portátil en «Recién llegado».
        departamento: p.departamento || undefined,
        subcategoria_elegida: p.subcategoria_elegida || undefined,
        proveedor: avisoProveedor,
        precio_costo: p.precio_costo,
        precio_venta: p.precio_venta || undefined,
        // El código del mayorista manda sobre el nombre para armar el SKU: es estable y es lo
        // que hay que darle cuando se le pide el producto.
        codigo_proveedor: p.codigo_proveedor || undefined,
        imagen_base64: fotos[0] || undefined,
        imagen_mime: 'image/jpeg',
        fotos: fotos.slice(1),      // las demás van a la galería de la ficha
      });
      cambiar({ estado: 'listo',
                msg: (data.creado ? 'Creado y publicado' : 'Actualizado y subido a novedades')
                     + (data.fotos ? ` · ${data.fotos} foto${data.fotos > 1 ? 's' : ''} más en la galería` : '') });
      cargar();
    } catch (e) {
      cambiar({ estado: 'pendiente', msg: e.response?.data?.error || 'No se pudo subir' });
    }
  };

  /* ══ LA BITACORA DE DIFUSIONES ══
   *
   * Victor manda por WhatsApp lo que le ofrecen los proveedores y eso no quedaba en ninguna
   * parte. Con 2.366 productos eso significa repetir los mismos dos semanas seguidas mientras
   * otros nunca salen, y no poder mirar que se le ofrecio a alguien que llama preguntando.
   *
   * Armar el mensaje y anotar la difusion van SEPARADOS a proposito: el sistema no puede
   * mandar WhatsApp por su cuenta, asi que anotar al armar el texto seria registrar cosas que
   * a lo mejor nunca salieron.
   */
  const [modalDif, setModalDif] = useState(false);
  const [difCands, setDifCands] = useState([]);
  const [difSel, setDifSel] = useState([]);
  const [difTexto, setDifTexto] = useState('');
  const [difWa, setDifWa] = useState('');
  const [difHist, setDifHist] = useState([]);
  const [difMsg, setDifMsg] = useState('');
  const [difVista, setDifVista] = useState('armar');
  const [difProv, setDifProv] = useState('');
  const [difBuscar, setDifBuscar] = useState('');
  const [difNuevos, setDifNuevos] = useState(false);

  /**
   * Ya NO se marcan solos los nunca difundidos.
   *
   * Tenía sentido cuando la lista eran los diez productos llegados por WhatsApp. Ahora sale
   * todo el catálogo vendible —1.438— y marcar solos los nunca difundidos sería marcar sesenta
   * de un golpe. Se escoge a mano, que es de lo que se trata.
   */
  const cargarCandidatos = async (filtros = {}) => {
    const q = new URLSearchParams();
    if (filtros.proveedor) q.set('proveedor', filtros.proveedor);
    if (filtros.buscar) q.set('buscar', filtros.buscar);
    if (filtros.desde_dias) q.set('desde_dias', String(filtros.desde_dias));
    const { data } = await api.get('/api/difusiones/candidatos?' + q.toString());
    setDifCands(data || []);
    return data || [];
  };

  const abrirDifusion = async () => {
    setModalDif(true); setDifTexto(''); setDifWa(''); setDifMsg(''); setDifVista('armar');
    setDifProv(''); setDifBuscar(''); setDifNuevos(false); setDifSel([]);
    try {
      const [, h] = await Promise.all([cargarCandidatos(), api.get('/api/difusiones')]);
      setDifHist(h.data || []);
    } catch (e) { setDifMsg(e.response?.data?.error || 'No se pudo cargar'); }
  };

  /** Cada filtro vuelve a preguntar: con 1.438 productos no se puede traer todo y filtrar aquí. */
  const filtrarDif = async (cambios) => {
    const prov = 'proveedor' in cambios ? cambios.proveedor : difProv;
    const txt  = 'buscar'    in cambios ? cambios.buscar    : difBuscar;
    const nue  = 'nuevos'    in cambios ? cambios.nuevos    : difNuevos;
    setDifProv(prov); setDifBuscar(txt); setDifNuevos(nue);
    try { await cargarCandidatos({ proveedor: prov, buscar: txt, desde_dias: nue ? 14 : 0 }); }
    catch (e) { setDifMsg(e.response?.data?.error || 'No se pudo cargar'); }
  };

  const alternarDif = (id) =>
    setDifSel(xs => (xs.includes(id) ? xs.filter(x => x !== id) : [...xs, id]));

  const armarDifusion = async () => {
    if (!difSel.length) return setDifMsg('Escoge al menos un producto');
    setDifMsg('');
    try {
      // El orden en que estan en la lista, no en el que se fueron tocando.
      const ids = difCands.filter(p => difSel.includes(p.id)).map(p => p.id);
      const { data } = await api.post('/api/difusiones/mensaje', { producto_ids: ids });
      setDifTexto(data.texto); setDifWa(data.whatsapp_url);
      try { await navigator.clipboard.writeText(data.texto); setDifMsg('Copiado — pégalo en WhatsApp'); }
      catch (e) { /* sin permiso: queda en pantalla para copiarlo a mano */ }
    } catch (e) { setDifMsg(e.response?.data?.error || 'No se pudo armar el mensaje'); }
  };

  /** Se anota DESPUES de mandarla. Anotar antes seria registrar lo que quiza nunca salio. */
  const anotarDifusion = async () => {
    try {
      const ids = difCands.filter(p => difSel.includes(p.id)).map(p => p.id);
      await api.post('/api/difusiones', { producto_ids: ids });
      setDifMsg('Anotada. Ya están en novedades, que es lo que abre el enlace del mensaje.');
      // Se recarga respetando los filtros puestos: si no, la lista saltaba a otra cosa.
      const [, h] = await Promise.all([
        cargarCandidatos({ proveedor: difProv, buscar: difBuscar, desde_dias: difNuevos ? 14 : 0 }),
        api.get('/api/difusiones'),
      ]);
      setDifHist(h.data || []); setDifSel([]);
      setDifTexto(''); setDifWa('');
    } catch (e) { setDifMsg(e.response?.data?.error || 'No se pudo anotar'); }
  };

  /** «hace 3 días», que es como uno piensa en esto. */
  const haceCuanto = (f) => {
    if (!f) return null;
    const d = Math.floor((Date.now() - new Date(f).getTime()) / 86400000);
    if (d <= 0) return 'hoy';
    if (d === 1) return 'ayer';
    if (d < 30) return `hace ${d} días`;
    return new Date(f).toLocaleDateString('es-CO');
  };

  /**
   * El mensaje para ofrecer el producto por WhatsApp.
   *
   * El texto lo arma el servidor: así el enlace del producto sale de la misma función que
   * usa la tienda, y el costo y la nota del proveedor no pueden colarse en algo que se le
   * manda a un cliente.
   *
   * Se copia al portapapeles Y se abre WhatsApp. Las dos cosas porque no siempre se quiere
   * lo mismo: a veces se reenvía ahí mismo y a veces se pega en otro lado.
   */
  const [waTexto, setWaTexto] = useState(null);

  const paraWhatsapp = async (p) => {
    try {
      const { data } = await api.get(`/api/catalogo/${p.id}/whatsapp`);
      setWaTexto(data);
      try { await navigator.clipboard.writeText(data.texto); } catch (e) { /* sin permiso: queda en pantalla para copiarlo a mano */ }
    } catch (e) { alert('No se pudo armar el mensaje: ' + (e.response?.data?.error || e.message)); }
  };

  /**
   * Las categorías del proveedor escogido, para las dos listas del formulario.
   *
   * Se piden al servidor cada vez que cambia el proveedor. Podrían pedirse una sola vez y
   * filtrarse aquí, pero entonces habría que saber en la pantalla qué categorías son de
   * quién — y esa es justo la clase de regla duplicada que ya nos costó una tarde con los
   * márgenes.
   */
  const [cats, setCats] = useState([]);

  useEffect(() => {
    if (!modal) return;
    let vivo = true;
    api.get('/api/catalogo/categorias-de', { params: { proveedor: form.proveedor } })
      .then(({ data }) => { if (vivo) setCats(Array.isArray(data) ? data : []); })
      .catch(() => { if (vivo) setCats([]); });
    return () => { vivo = false; };
  }, [modal, form.proveedor]);

  const subcatsDisponibles = (cats.find(c => c.categoria === form.categoria) || {}).subcategorias || [];

  /** Un SKU legible a partir del nombre, para los que llegan por WhatsApp sin código. */
  const sugerirSku = async () => {
    // Un prefijo por proveedor, decididos por Victor. El de El Paisa es el mismo que usa
    // su importador (`services/importarElPaisa.js`): un producto puesto a mano y uno
    // importado tienen que verse igual en el catalogo.
    const pref = PREFIJO_SKU[form.proveedor] || 'TP';
    const base = (form.nombre || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 28);
    if (!base) return setMsg('Escribe primero el nombre y te armo el SKU');
    /**
     * Se le pregunta al servidor, que además comprueba que el código no choque con uno que ya
     * exista — cosa que desde aquí no se puede saber. Lo de arriba queda solo como respaldo
     * si la consulta falla: la regla de verdad vive en `services/altaDesdeAviso.js`, porque
     * al hacer el alta desde el aviso quedaron dos copias y el mismo producto habría tenido
     * un código distinto según por dónde se creó.
     */
    try {
      const { data } = await api.get('/api/catalogo/sku-sugerido',
        { params: { nombre: form.nombre, proveedor: form.proveedor } });
      if (data?.sku) return setForm(p => ({ ...p, sku: data.sku }));
    } catch (e) { /* sin servidor, vale el de aquí abajo */ }
    setForm(p => ({ ...p, sku: `${pref}-${base}` }));
  };

  const VACIO = { sku:'', nombre:'', categoria:'', subcategoria:'', precio_costo:'', precio_venta:'',
                  precio_antes:'', proveedor:'Tecnoplaza', descripcion:'', imagen_base64:'',
                  fotos:[], en_tienda:true };

  const abrirModal = (p=null) => {
    if (p) setForm({ ...VACIO, sku:p.sku, nombre:p.nombre, categoria:p.categoria||'', subcategoria:p.subcategoria||'',
                     precio_costo:p.precio_costo, precio_venta:p.precio_venta, precio_antes:p.precio_antes||'',
                     proveedor:p.proveedor||'Tecnoplaza', en_tienda:p.en_tienda !== false });
    else setForm(VACIO);
    setEditando(p);
    setSugerido(null);
    setPrecioTocado(Boolean(p));   // al editar no se le toca el precio que ya tenía
    setMsg('');
    setModal(true);
  };

  /**
   * Sacar o meter un producto en la tienda, sin tocar nada más.
   *
   * Se manda el producto completo porque el endpoint de guardar espera todos los campos;
   * mandar solo `en_tienda` le borraría el precio y la categoría. Se cambia la lista en
   * pantalla al instante y se recarga después: si el guardado falla, la recarga lo devuelve
   * a como estaba y no queda diciendo una cosa mientras la base dice otra.
   */
  const alternarTienda = async (p) => {
    const nuevo = p.en_tienda === false;
    setProductos(prev => prev.map(x => x.id === p.id ? { ...x, en_tienda: nuevo } : x));
    try {
      await api.put(`/api/catalogo/${p.id}`, {
        sku: p.sku, nombre: p.nombre, categoria: p.categoria,
        precio_costo: p.precio_costo, precio_venta: p.precio_venta,
        precio_antes: p.precio_antes, activo: p.activo, proveedor: p.proveedor,
        en_tienda: nuevo,
      });
    } catch (e) {
      alert('No se pudo cambiar: ' + (e.response?.data?.error || e.message));
    }
    cargar();
  };

  const guardar = async () => {
    if (!form.sku || !form.nombre || !form.precio_costo) return setMsg('SKU, nombre y precio costo son requeridos');
    try {
      if (editando) await api.put(`/api/catalogo/${editando.id}`, form);
      else await api.post('/api/catalogo', form);
      setModal(false);
      cargar();
    } catch(e) { setMsg(e.response?.data?.error || 'Error al guardar'); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar producto?')) return;
    await api.delete(`/api/catalogo/${id}`);
    cargar();
  };

  const [archivoPDF, setArchivoPDF] = useState(null);

  const importarPDF = async () => {
    if (!archivoPDF) return setMsgImport('Selecciona un archivo PDF');
    setImportando(true);
    setMsgImport('');
    try {
      const formData = new FormData();
      formData.append('archivo', archivoPDF);
      formData.append('proveedor', proveedorImport);
      const r = await api.post('/api/catalogo/importar-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMsgImport(r.data.mensaje);
      cargar();
    } catch(e) { setMsgImport(e.response?.data?.error || 'Error al importar el PDF'); }
    setImportando(false);
  };

  // El precio de venta ya no se calcula aquí: lo trae el servidor (ver `sugerido` arriba).
  const f = (k,v) => setForm(p => ({ ...p, [k]: v }));

  const totalPages = Math.ceil(total/LIMIT);

  const s = {
    page:  { padding:'24px', maxWidth:'1280px', margin:'0 auto' },
    card:  { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'12px', padding:'20px', marginBottom:'16px' },
    h1:    { fontSize:'22px', fontWeight:'700', margin:0, color:'#fff' },
    btn:   { padding:'8px 16px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'13px' },
    btnP:  { background:'#2563EB', color:'#fff' },
    btnG:  { background:'#16A34A', color:'#fff' },
    btnR:  { background:'#DC2626', color:'#fff' },
    btnW:  { background:'rgba(255,255,255,0.08)', color:'rgba(255,255,255,0.85)' },
    /**
     * Botón secundario sobre fondo oscuro, con borde.
     *
     * `btn` NO trae fondo ni color —solo el relleno y el borde redondeado—, así que usado
     * solo hereda el gris claro del navegador. Con letra blanca encima queda **ilegible**, y
     * así salieron las pestañas y los botones de escoger de las ventanas nuevas.
     */
    btnS:  { background:'rgba(255,255,255,0.07)', color:'#fff', border:'1px solid rgba(255,255,255,0.18)' },
    /** El mismo, cuando está escogido. */
    btnSel:{ background:'rgba(0,229,255,0.15)', color:'#00E5FF', border:'1px solid rgba(0,229,255,0.5)' },
    btnO:  { background:'#D97706', color:'#fff' },
    btnPink:{ background:'#EC4899', color:'#fff' },
    input: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'9px 12px', color:'rgba(255,255,255,0.9)', fontSize:'13px', outline:'none', width:'100%', boxSizing:'border-box', fontFamily:'inherit' },
    label: { fontSize:'11px', fontWeight:'700', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.8px', display:'block', marginBottom:'4px' },
    th:    { textAlign:'left', padding:'10px 12px', fontSize:'11px', fontWeight:'700', color:'rgba(255,255,255,0.4)', borderBottom:'1px solid rgba(255,255,255,0.08)', textTransform:'uppercase', letterSpacing:'0.5px' },
    td:    { padding:'10px 12px', fontSize:'13px', borderBottom:'1px solid rgba(255,255,255,0.05)', color:'rgba(255,255,255,0.8)', verticalAlign:'middle' },
    grid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' },
  };


  /**
   * Una foto a 420px, que es la medida de siempre en el catálogo.
   *
   * Estaba escrita tres veces —aquí, en «+ Nuevo producto» y en la ventana del WhatsApp— con
   * el mismo número en las tres. Una regla repetida en este proyecto siempre termina
   * separándose, y una foto pesaría distinto según por dónde se subió.
   */
  const encogerFoto = (file, max = 420, calidad = 0.82) => new Promise((listo) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      const escala = Math.min(max / img.width, max / img.height, 1);
      c.width = Math.round(img.width * escala); c.height = Math.round(img.height * escala);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      listo(c.toDataURL('image/jpeg', calidad).split(',')[1]);
    };
    img.onerror = () => listo(null);
    img.src = URL.createObjectURL(file);
  });

  /**
   * El botón 📷 de cada producto, ahora con varias.
   *
   * La PRIMERA reemplaza la foto principal —la que sale en la tarjeta de la tienda— y las
   * demás se agregan a la galería de la ficha, que ya existía y estaba casi vacía. Se
   * agregan, no reemplazan: borrar las que ya estaban sería perder trabajo hecho.
   */
  const subirFoto = (producto) => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'image/*';
    inp.multiple = true;
    inp.onchange = async () => {
      const files = [...(inp.files || [])];
      if (!files.length) return;
      const b64s = [];
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        const b = await encogerFoto(f);
        if (b) b64s.push(b);
      }
      if (!b64s.length) return alert('No se pudo leer ninguna de esas imágenes');
      try {
        await api.put(`/api/catalogo/${producto.id}/imagen`, { imagen_base64: b64s[0], imagen_mime: 'image/jpeg' });
        let extra = 0;
        if (b64s.length > 1) {
          const { data } = await api.post(`/api/catalogo/${producto.id}/fotos`, { fotos: b64s.slice(1) });
          extra = data.fotos || 0;
        }
        alert(extra
          ? `📷 Foto principal cambiada y ${extra} más agregadas a la galería — ya se ven en la tienda`
          : '📷 Foto guardada — ya se ve en la tienda web');
        cargar();
      } catch(e) { alert(e.response?.data?.error || 'Error al subir las fotos'); }
    };
    inp.click();
  };


  const buscarFotos = async () => {
    if (!window.confirm('¿Buscar automáticamente fotos para los productos Tecnoplaza sin imagen? Tarda 5-15 minutos en segundo plano.')) return;
    try {
      const { data } = await api.post('/api/catalogo/buscar-fotos');
      alert('📸 ' + data.mensaje);
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };


  const importarWeb = async () => {
    if (!window.confirm('Importar TODO el catálogo desde la tienda web de Tecnoplaza (nombres, precios web, fotos, marcas y categorías reales)?\n\nLos productos de otros proveedores quedarán inactivos en la tienda. Tarda 10-20 min en segundo plano.')) return;
    try {
      const { data } = await api.post('/api/catalogo/importar-web');
      alert('🌐 ' + data.mensaje);
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };


  const importarIlum = async () => {
    if (!window.confirm('Importar los productos de IluminaLEDs desde tu página Wix (con precios y fotos)? Tarda 5-10 min en segundo plano.')) return;
    try {
      const { data } = await api.post('/api/catalogo/importar-iluminaleds');
      alert('💡 ' + data.mensaje);
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  /**
   * El Paisa no publica lista descargable: su catálogo se lee con el raspador que corre en
   * este computador (`raspador/raspar_elpaisa.js`) y deja un `elpaisa.json`. Ese archivo se
   * sube aquí, igual que el PDF de Tecnoplaza.
   */
  /**
   * Acomodar el catalogo en los departamentos que ve el cliente.
   *
   * Se corre una vez despues de desplegar; de ahi en adelante los importadores dejan cada
   * producto en su sitio solos. Es repetible sin daño.
   */
  const reorganizar = async () => {
    if (!window.confirm('¿Reacomodar los productos publicados en los departamentos de la tienda?\n\n' +
      'No cambia precios ni direcciones de producto: solo en qué pestaña aparece cada uno.')) return;
    try {
      const { data } = await api.post('/api/catalogo/reorganizar');
      alert('🗂️ ' + data.mensaje + '\n\n' +
        (data.departamentos || []).map(d => `${d.nombre}: ${d.total}`).join('\n'));
      cargar();
    } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const importarElPaisa = () => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = '.json,application/json';
    inp.onchange = async () => {
      const file = inp.files[0]; if (!file) return;
      if (!window.confirm(`Importar «${file.name}» como catálogo de El Paisa?\n\nEntra todo en la pestaña Variedades. Se guarda un respaldo antes de tocar nada.`)) return;
      try {
        const fd = new FormData();
        fd.append('archivo', file);
        const { data } = await api.post('/api/catalogo/importar-elpaisa', fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
        alert('🧰 ' + data.mensaje);
        cargar();
      } catch(e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    };
    inp.click();
  };

  return (
    <Layout>
      <div style={s.page}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', flexWrap:'wrap', gap:'10px' }}>
          <div>
            <h1 style={s.h1}>📦 Catálogo de Productos</h1>
            <p style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', margin:'4px 0 0' }}>{total} productos</p>
          </div>
          {esAdmin && (
            <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
              <button style={{...s.btn,...s.btnO}} onClick={()=>{setTextoPDF('');setMsgImport('');setProveedorImport('Tecnoplaza');setModalImport(true);}}>📋 Actualizar Tecnoplaza</button>
              <button style={{...s.btn, background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.3)', color:'#A5B4FC'}}
                onClick={()=>window.location.href='/catalogo/costos'}
                title="Ver con qué producto del PDF cruzó cada uno y corregir los que estén mal">🔍 Revisar costos del PDF</button>
              <button style={{...s.btn, background:'rgba(20,184,166,0.12)', border:'1px solid rgba(20,184,166,0.3)', color:'#5EEAD4'}}
                onClick={()=>window.location.href='/catalogo/comparar'}
                title="A cuál proveedor conviene comprarle cada producto que ambos venden">⚖️ Comparar proveedores</button>
            <button style={{...s.btn, background:'rgba(168,85,247,0.12)', border:'1px solid rgba(168,85,247,0.3)', color:'#C084FC'}} onClick={buscarFotos}>🖼️ Buscar fotos auto</button>
            <button style={{...s.btn, background:'rgba(20,184,166,0.15)', border:'1px solid rgba(20,184,166,0.4)', color:'#5EEAD4', fontWeight:'700'}} onClick={importarWeb}>🌐 Importar desde tienda Tecnoplaza</button>
            <button style={{...s.btn, background:'rgba(250,204,21,0.12)', border:'1px solid rgba(250,204,21,0.35)', color:'#FDE047'}} onClick={importarIlum}>💡 Importar IluminaLEDs</button>
            <button style={{...s.btn, background:'rgba(56,189,248,0.12)', border:'1px solid rgba(56,189,248,0.35)', color:'#7DD3FC'}} onClick={reorganizar}
              title="Acomoda los productos publicados en los departamentos que ve el cliente">🗂️ Reorganizar tienda</button>
            <button style={{...s.btn, background:'rgba(249,115,22,0.12)', border:'1px solid rgba(249,115,22,0.35)', color:'#FDBA74'}} onClick={importarElPaisa}
              title="Sube el elpaisa.json que deja el raspador — entra todo en Variedades">🧰 Importar El Paisa</button>
              <button style={{...s.btn,...s.btnPink}} onClick={()=>{setTextoPDF('');setMsgImport('');setProveedorImport('Metro');setModalImport(true);}}>📋 Actualizar Metro</button>
              {/* Lo de todos los días: la oferta que mandan por WhatsApp. Va primero porque
                  es lo que más se usa, y las importaciones son semanales. */}
              <button style={{...s.btn, background:'rgba(34,197,94,0.14)', border:'1px solid rgba(34,197,94,0.4)', color:'#86EFAC', fontWeight:'700'}}
                onClick={abrirAviso}
                title="Sube la imagen que te mandó el proveedor y publica lo que trae">📸 Del WhatsApp</button>
              <button style={{...s.btn, background:'rgba(0,229,255,0.12)', border:'1px solid rgba(0,229,255,0.35)', color:'#67E8F9', fontWeight:'700'}}
                onClick={abrirDifusion}
                title="Arma la difusión y anota qué mandaste y cuándo">📣 Difusión</button>
              <button style={{...s.btn,...s.btnP}} onClick={()=>abrirModal()}>+ Nuevo producto</button>
            </div>
          )}
        </div>

        {/* Filtros */}
        <div style={{ ...s.card, display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center' }}>
          <input style={{ ...s.input, maxWidth:'280px' }} placeholder="🔍 Buscar por nombre o SKU..." value={busqueda}
            onChange={e=>{ setBusq(e.target.value); setPage(1); }}/>
          <select style={{ ...s.input, width:'auto' }} value={provFiltro} onChange={e=>{ setProvFiltro(e.target.value); setPage(1); }}>
            <option value="">Todos los proveedores</option>
            {proveedores.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
          <select style={{ ...s.input, width:'auto' }} value={catFiltro} onChange={e=>{ setCatFiltro(e.target.value); setPage(1); }}>
            <option value="">Todas las categorías</option>
            {categorias.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <span style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)' }}>
            Mostrando {productos.length} de {total}
          </span>
        </div>

        {/* Tabla */}
        <div style={s.card}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={s.th}>SKU</th>
                <th style={s.th}>Nombre</th>
                <th style={s.th}>Proveedor</th>
                <th style={s.th}>Categoría</th>
                <th style={s.th} title="Costo real del mayorista, tomado de la lista en PDF">Precio PDF</th>
                <th style={s.th} title="Precio de la página web del proveedor: sobre este se calcula la venta">Precio web</th>
                <th style={s.th}>Precio venta</th>
                <th style={s.th} title="Ganancia real: precio de venta menos el costo del PDF">Ganancia real</th>
                <th style={s.th} title="Margen sobre el costo real del mayorista">Margen real</th>
                {esAdmin && <th style={s.th}>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {productos.length === 0 ? (
                <tr><td colSpan="10" style={{ ...s.td, textAlign:'center', padding:'40px', color:'rgba(255,255,255,0.3)' }}>
                  No hay productos. Importa desde PDF o agrega manualmente.
                </td></tr>
              ) : productos.map(p=>(
                <tr key={p.id}>
                  <td style={s.td}>
                    <code style={{ background:'rgba(255,255,255,0.06)', padding:'2px 6px', borderRadius:'4px', fontSize:'12px', color:'#00D4FF' }}>
                      {p.sku}
                    </code>
                  </td>
                  <td style={{ ...s.td, maxWidth:'320px' }}>
                    <div style={{ fontSize:'13px', fontWeight:'500', lineHeight:'1.3' }}>{p.nombre}</div>
                    {/* Lo que el proveedor NO vende por unidad («PACA X 60 UND»). No sale a
                        la tienda —el cliente creería que le llegan 60— pero al comprar
                        importa, así que se ve aquí. */}
                    {p.nota_proveedor && (
                      <div style={{ fontSize:'11px', color:'#FDBA74', marginTop:'3px' }}
                           title="Presentación en la que el proveedor lo vende">
                        📦 {p.nota_proveedor}
                      </div>
                    )}
                  </td>
                  <td style={s.td}>
                    <span style={{ background:(PROV_COLOR[p.proveedor]||'#6B7280')+'25', color:PROV_COLOR[p.proveedor]||'#9CA3AF', padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'700' }}>
                      {p.proveedor}
                    </span>
                    {/* En la tienda o solo por dentro. Es distinto de «activo»: un producto
                        puede seguir vivo para cotizar y comparar proveedores sin publicarse
                        en linkustelecom.com — es el caso de todo Metro. */}
                    <button type="button" onClick={() => alternarTienda(p)}
                      title={p.en_tienda === false
                        ? 'No se publica en la tienda. Clic para publicarlo.'
                        : 'Se publica en la tienda. Clic para sacarlo.'}
                      style={{ display:'block', marginTop:'5px', cursor:'pointer', border:0,
                        fontFamily:'inherit', fontSize:'10.5px', fontWeight:700,
                        padding:'2px 8px', borderRadius:'10px',
                        background: p.en_tienda === false ? 'rgba(148,163,184,0.15)' : 'rgba(34,197,94,0.15)',
                        color: p.en_tienda === false ? '#94A3B8' : '#4ADE80' }}>
                      {p.en_tienda === false ? '🚫 fuera de la tienda' : '🛒 en la tienda'}
                    </button>
                  </td>
                  <td style={s.td}>
                    <span style={{ background:'rgba(79,70,229,0.15)', color:'#A5B4FC', padding:'2px 8px', borderRadius:'10px', fontSize:'11px' }}>
                      {p.categoria || '—'}
                    </span>
                  </td>
                  <td style={s.td}>
                    {p.precio_pdf
                      ? fmt(p.precio_pdf)
                      : <span title="Aún no se ha importado la lista PDF de este proveedor"
                              style={{ color:'rgba(255,255,255,0.3)', fontSize:'12px' }}>sin PDF</span>}
                  </td>
                  <td style={s.td}>{fmt(p.precio_costo)}</td>
                  <td style={{ ...s.td, fontWeight:'700', color:'#22C55E' }}>{fmt(p.precio_venta)}</td>
                  <td style={s.td}>
                    {/* Sin costo conocido no hay ganancia que mostrar: va «—», no $0.
                        En Tecnoplaza el costo es el del PDF mayorista y su lista no cubre
                        todo el catálogo web, así que esto pasa de verdad. */}
                    <span style={{
                      color: p.ganancia_real == null ? 'rgba(255,255,255,0.3)'
                           : Number(p.ganancia_real) > 0 ? '#22C55E' : '#EF4444',
                      fontSize:'12.5px', fontWeight:'600'
                    }} title={p.ganancia_real == null ? 'No se conoce el costo real de este producto' : undefined}>
                      {p.ganancia_real == null ? '—' : fmt(p.ganancia_real)}
                    </span>
                  </td>
                  <td style={s.td}>
                    {/* Si todavía no hay precio del PDF, el margen real se calcula contra el
                        precio web y por eso puede verse muy bajo: se avisa con el color. */}
                    <span style={{
                      color: !p.precio_pdf ? 'rgba(255,255,255,0.35)'
                           : Number(p.margen_real) >= 10 ? '#22C55E'
                           : Number(p.margen_real) > 0 ? '#F59E0B' : '#EF4444',
                      fontSize:'12px', fontWeight:'700'
                    }}>
                      {p.margen_real != null ? `${p.margen_real}%` : '—'}
                    </span>
                    <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.3)' }}>aplicado {p.margen_aplicado}%</div>
                  </td>
                  {esAdmin && (
                    <td style={s.td}>
                      <div style={{ display:'flex', gap:'6px' }}>
                        <button style={{...s.btn,...s.btnW,padding:'5px 10px'}} onClick={()=>abrirModal(p)}>✏️</button>
                        <button title="Subir foto del producto (tienda web)" style={{...s.btn,...s.btnW,padding:'5px 10px'}} onClick={()=>subirFoto(p)}>📷</button>
                        <button title="Armar el mensaje para ofrecerlo por WhatsApp" style={{...s.btn,background:'rgba(37,211,102,0.15)',border:'1px solid rgba(37,211,102,0.4)',color:'#4ADE80',padding:'5px 10px'}} onClick={()=>paraWhatsapp(p)}>💬</button>
                        {usuario?.rol==='superadmin' && <button style={{...s.btn,...s.btnR,padding:'5px 10px'}} onClick={()=>eliminar(p.id)}>🗑️</button>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={{ display:'flex', justifyContent:'center', gap:'8px', marginTop:'16px' }}>
              <button style={{...s.btn,...s.btnW}} disabled={page===1} onClick={()=>setPage(p=>p-1)}>← Anterior</button>
              <span style={{ padding:'8px 16px', fontSize:'13px', color:'rgba(255,255,255,0.5)' }}>
                Página {page} de {totalPages}
              </span>
              <button style={{...s.btn,...s.btnW}} disabled={page===totalPages} onClick={()=>setPage(p=>p+1)}>Siguiente →</button>
            </div>
          )}
        </div>

        {/* Modal crear/editar */}
        {/* ══ La bitacora de difusiones ══
            Armar el mensaje y anotarlo van separados: el sistema no manda WhatsApp por su
            cuenta, asi que anotar al armar seria registrar lo que quiza nunca salio. */}
        {modalDif && (
          <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:500,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'16px',overflowY:'auto' }}>
            <div style={{ background:'#0F1629',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'14px',padding:'22px',width:'100%',maxWidth:'660px',marginTop:'10px' }}>
              <div style={{ display:'flex',alignItems:'center',gap:'10px',marginBottom:'14px' }}>
                <h2 style={{ fontSize:'16px',fontWeight:'700',color:'#fff' }}>📣 Difusión por WhatsApp</h2>
                <button style={{...s.btn, ...s.btnS, marginLeft:'auto'}} onClick={()=>setModalDif(false)}>Cerrar</button>
              </div>

              <div style={{ display:'flex', gap:'8px', marginBottom:'14px' }}>
                {[['armar','Armar la difusión'],['historial',`Qué he mandado (${difHist.length})`]].map(([v,t]) => (
                  <button key={v} onClick={()=>setDifVista(v)}
                    style={{ ...s.btn, ...(difVista===v ? s.btnSel : s.btnS) }}>{t}</button>
                ))}
              </div>

              {difMsg && <div style={{ background:'rgba(0,229,255,0.1)',color:'#67E8F9',padding:'10px',borderRadius:'8px',marginBottom:'12px',fontSize:'13px' }}>{difMsg}</div>}

              {difVista === 'armar' && (
                <>
                  <div style={{ fontSize:'12.5px', color:'rgba(255,255,255,0.5)', marginBottom:'10px', lineHeight:'1.6' }}>
                    Escoge lo que vas a promocionar. Arriba salen los que <b>llevan más tiempo sin
                    difundirse</b>; los que ya mandaste dicen cuándo, para no repetirlos.
                    <div style={{ marginTop:'5px', color:'rgba(255,255,255,0.45)' }}>
                      Lo que difundas aparecerá en la página de novedades, que es la que abre el
                      enlace del mensaje.
                    </div>
                  </div>

                  {/* Con 1.438 productos vendibles, sin filtros no se escoge nada. */}
                  <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'10px' }}>
                    <input value={difBuscar} placeholder="Buscar por nombre…"
                      onChange={(e)=>setDifBuscar(e.target.value)}
                      onKeyDown={(e)=>{ if (e.key === 'Enter') filtrarDif({ buscar: e.target.value }); }}
                      onBlur={(e)=>filtrarDif({ buscar: e.target.value })}
                      style={{ ...s.input, flex:'1 1 170px', minWidth:0 }} />
                    <select value={difProv} onChange={(e)=>filtrarDif({ proveedor: e.target.value })}
                      style={{ ...s.input, flex:'0 1 150px' }}>
                      <option value="">Todos los proveedores</option>
                      <option value="El Paisa">El Paisa</option>
                      <option value="Tecnoplaza">Tecnoplaza</option>
                      <option value="IluminaLEDs">IluminaLEDs</option>
                      <option value="Metro">Metro</option>
                    </select>
                    <button type="button" onClick={()=>filtrarDif({ nuevos: !difNuevos })}
                      style={{ ...s.btn, ...(difNuevos ? s.btnSel : s.btnS) }}>
                      {difNuevos ? '✓ ' : ''}Lo que entró hace poco
                    </button>
                  </div>

                  {!difCands.length && (
                    <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', padding:'20px 0' }}>
                      Nada con esos filtros. Prueba con otro proveedor o borra la búsqueda.
                    </div>
                  )}

                  <div style={{ maxHeight:'320px', overflowY:'auto', marginBottom:'12px' }}>
                    {difCands.map(p => (
                      <label key={p.id} style={{ display:'flex', gap:'10px', alignItems:'center', padding:'9px 4px',
                                                 borderBottom:'1px solid rgba(255,255,255,0.06)', cursor:'pointer' }}>
                        <input type="checkbox" checked={difSel.includes(p.id)} onChange={()=>alternarDif(p.id)}
                          style={{ width:'auto', accentColor:'#00E5FF' }} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:'13px', color:'#fff' }}>{p.nombre}</div>
                          <div style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.4)' }}>
                            ${Number(p.precio_venta).toLocaleString('es-CO')}
                            {/* El proveedor solo se ve aquí adentro, nunca en la tienda. */}
                            {p.proveedor && <span> · {p.proveedor}</span>}
                            {p.veces_difundido > 0
                              ? <span style={{ color:'#FCD34D' }}> · difundido {haceCuanto(p.difundido_en)}{p.veces_difundido > 1 ? ` (${p.veces_difundido} veces)` : ''}</span>
                              : <span style={{ color:'#86EFAC' }}> · nunca difundido</span>}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  <button style={{...s.btn,...s.btnP, width:'100%', padding:'12px'}}
                    onClick={armarDifusion} disabled={!difSel.length}>
                    ✍️ Armar el mensaje ({difSel.length})
                  </button>

                  {difTexto && (
                    <div style={{ marginTop:'14px' }}>
                      <label style={s.label}>El mensaje — ya está copiado</label>
                      <textarea readOnly value={difTexto}
                        style={{ ...s.input, minHeight:'170px', fontFamily:'inherit', lineHeight:'1.5', fontSize:'12.5px' }} />
                      <div style={{ display:'flex', gap:'10px', marginTop:'10px' }}>
                        <a href={difWa} target="_blank" rel="noopener"
                          style={{ ...s.btn, flex:1, textAlign:'center', background:'rgba(37,211,102,0.15)',
                                   border:'1px solid rgba(37,211,102,0.4)', color:'#4ADE80', padding:'12px', fontWeight:'700' }}>
                          💬 Abrir WhatsApp
                        </a>
                        <button style={{...s.btn,...s.btnP, flex:1, padding:'12px'}} onClick={anotarDifusion}>
                          ✅ Ya la mandé — anótala
                        </button>
                      </div>
                      <div style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.4)', marginTop:'8px', lineHeight:'1.5' }}>
                        Anótala <b>después</b> de mandarla. El sistema no puede mandar WhatsApp por su cuenta,
                        así que anotarla antes sería registrar algo que quizá nunca salió.
                      </div>
                    </div>
                  )}
                </>
              )}

              {difVista === 'historial' && (
                <div style={{ maxHeight:'440px', overflowY:'auto' }}>
                  {!difHist.length && (
                    <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', padding:'20px 0' }}>
                      Todavía no has anotado ninguna difusión.
                    </div>
                  )}
                  {difHist.map(d => (
                    <div key={d.id} style={{ border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px',
                                             padding:'12px', marginBottom:'10px' }}>
                      <div style={{ display:'flex', gap:'10px', alignItems:'baseline' }}>
                        <b style={{ fontSize:'13px', color:'#67E8F9' }}>{new Date(d.creado_en).toLocaleString('es-CO')}</b>
                        <span style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.4)' }}>
                          {d.cuantos} producto{d.cuantos === 1 ? '' : 's'}{d.quien ? ` · ${d.quien}` : ''}
                        </span>
                      </div>
                      <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.55)', marginTop:'6px', lineHeight:'1.6' }}>
                        {(d.productos || []).map(p => p.nombre).filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ Del WhatsApp: la oferta que manda el proveedor, subida a la tienda ══
            Una sola columna y botones grandes: esto se usa en el celular, que es donde llega
            el WhatsApp. */}
        {modalAviso && (
          <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:500,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'16px',overflowY:'auto' }}>
            <div style={{ background:'#0F1629',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'14px',padding:'22px',width:'100%',maxWidth:'620px',marginTop:'10px' }}>
              <div style={{ display:'flex',alignItems:'center',gap:'10px',marginBottom:'16px' }}>
                <h2 style={{ fontSize:'16px',fontWeight:'700',color:'#fff' }}>📸 Del WhatsApp a la tienda</h2>
                <button style={{...s.btn, ...s.btnS, marginLeft:'auto'}} onClick={()=>setModalAviso(false)}>Cerrar</button>
              </div>

              {avisoMsg && <div style={{ background:'rgba(239,68,68,0.1)',color:'#FCA5A5',padding:'10px',borderRadius:'8px',marginBottom:'14px',fontSize:'13px',lineHeight:'1.5' }}>{avisoMsg}</div>}

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                <button style={{...s.btn,...s.btnW}} onClick={escogerFotoAviso}>
                  {fotos.length ? `🖼️ Agregar más (${fotos.length})` : '🖼️ Escoger imágenes'}
                </button>
                <select style={s.input} value={avisoProveedor} onChange={e=>setAvisoProveedor(e.target.value)}>
                  {['El Paisa','Tecnoplaza','Metro','IluminaLEDs'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              {/* El texto es la fuente buena. «Precio Mayorista:$14.500» se lee exacto; el
                  mismo numero sacado de la foto siempre es una interpretacion. */}
              <label style={s.label}>Pega aquí el mensaje que te mandaron</label>
              <textarea style={{ ...s.input, minHeight:'110px', fontFamily:'inherit', lineHeight:'1.5' }}
                value={avisoTexto} onChange={e=>setAvisoTexto(e.target.value)}
                placeholder={'RELOJ X7\n💰Precio Mayorista:$14.500\n💰Precio Al Detal:$28.000\n· Pantalla táctil…'} />

              {/* Varias fotos del mismo producto. La primera es la principal —la que sale en
                  la tarjeta de la tienda— y las demás van a la galería de la ficha. */}
              {fotos.length > 0 && (
                <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginTop:'12px' }}>
                  {fotos.map((f, i) => (
                    <div key={i} style={{ position:'relative', paddingBottom:'12px' }}>
                      <img src={`data:image/jpeg;base64,${f}`} alt=""
                        onClick={()=>hacerPrincipal(i)}
                        title={i === 0 ? 'Es la que sale en la tienda' : 'Tocar para que ésta sea la principal'}
                        style={{ width:'70px',height:'70px',objectFit:'contain',background:'#fff',borderRadius:'9px',cursor:'pointer',
                        border: i === 0 ? '2px solid #00E5FF' : '1px solid rgba(255,255,255,0.12)' }} />
                      {i === 0 && <div style={{ position:'absolute',bottom:0,left:0,right:0,textAlign:'center',fontSize:'9px',color:'#00E5FF',fontWeight:'700' }}>PRINCIPAL</div>}
                      <button onClick={()=>quitarFoto(i)} title="Quitar"
                        style={{ position:'absolute',top:'-6px',right:'-6px',width:'20px',height:'20px',borderRadius:'50%',
                                 background:'#EF4444',color:'#fff',border:'none',cursor:'pointer',fontSize:'12px',lineHeight:'1' }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {fotos.length > 1 && (
                <div style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.45)', marginTop:'4px', lineHeight:'1.5' }}>
                  <b>Toca una foto para que sea la principal.</b> La del aviso lleva el logo del proveedor
                  y letras encima; una foto limpia del producto se ve mejor en la tienda. Las otras {fotos.length - 1} van a la galería.
                </div>
              )}

              <div style={{ display:'flex', gap:'12px', alignItems:'center', marginTop:'12px', marginBottom:'14px' }}>
                <button style={{...s.btn,...s.btnP, flex:1, padding:'13px'}} onClick={leerAviso} disabled={avisoLeyendo}>
                  {avisoLeyendo ? 'Leyendo…' : '✨ Leer el mensaje'}
                </button>
              </div>

              {ficha && (
                <div style={{ border:'1px solid rgba(255,255,255,0.12)', borderRadius:'12px', padding:'14px',
                              background: ficha.estado==='listo' ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.02)' }}>
                  {ficha.estado === 'listo' ? (
                    <div style={{ color:'#86EFAC', fontSize:'13.5px' }}>✅ <b>{ficha.nombre}</b> — {ficha.msg}</div>
                  ) : (
                    <>
                      {(ficha.avisos || []).map((a, k) => (
                        <div key={k} style={{ background:'rgba(245,158,11,0.12)',color:'#FCD34D',padding:'9px',borderRadius:'8px',marginBottom:'10px',fontSize:'12.5px' }}>⚠️ {a}</div>
                      ))}

                      <label style={s.label}>Nombre</label>
                      <input style={s.input} value={ficha.nombre || ''} onChange={e=>cambiar({nombre:e.target.value})}
                        placeholder="El proveedor no lo dijo — escríbelo" />
                      {/* De dónde salió cada cosa. Lo leído del texto es exacto; lo de la
                          imagen es interpretación y hay que mirarlo. */}
                      {ficha.fuente_nombre === 'imagen' && (
                        <div style={{ fontSize:'11.5px', color:'#FCD34D', marginTop:'5px' }}>
                          ⚠️ Ese nombre salió de la <b>imagen</b>, no del mensaje. Revísalo.
                        </div>
                      )}

                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'12px' }}>
                        <div>
                          <label style={s.label}>Te cuesta *</label>
                          <input style={s.input} inputMode="numeric" value={ficha.precio_costo ?? ''}
                            onChange={e=>{ const v = e.target.value.replace(/[^0-9]/g,''); cambiar({precio_costo: v}); pedirSugerido(v); }} />
                        </div>
                        <div>
                          <label style={s.label}>Lo vendes en</label>
                          <input style={s.input} inputMode="numeric" value={ficha.precio_venta ?? ''}
                            onChange={e=>cambiar({precio_venta: e.target.value.replace(/[^0-9]/g,'')})}
                            placeholder={sugeridoAviso ? String(Math.round(sugeridoAviso.precio_venta)) : 'lo calcula tu escala'} />
                        </div>
                      </div>

                      {/* Los dos precios, uno al lado del otro. El proveedor sugiere uno y tu
                          escala calcula otro; verlos juntos es lo que permite decidir. */}
                      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginTop:'8px' }}>
                        {sugeridoAviso && (
                          <button style={{...s.btn, ...s.btnS, fontSize:'12px'}} onClick={()=>cambiar({precio_venta: String(Math.round(sugeridoAviso.precio_venta))})}>
                            Tu escala: ${Number(sugeridoAviso.precio_venta).toLocaleString('es-CO')}
                            {sugeridoAviso.margen_pct ? ` (+${sugeridoAviso.margen_pct}%)` : ''}
                          </button>
                        )}
                        {ficha.precio_detal > 0 && (
                          <button style={{...s.btn, fontSize:'12px', background:'rgba(34,197,94,0.12)',
                                          border:'1px solid rgba(34,197,94,0.4)', color:'#86EFAC'}}
                            onClick={()=>cambiar({precio_venta: String(ficha.precio_detal)})}>
                            El proveedor sugiere: ${Number(ficha.precio_detal).toLocaleString('es-CO')}
                          </button>
                        )}
                      </div>
                      {ficha.precio_de_imagen && (
                        <div style={{ fontSize:'11.5px', color:'#FCD34D', marginTop:'7px' }}>
                          ⚠️ Ese precio salió de la <b>imagen</b>, no del mensaje. Confírmalo.
                        </div>
                      )}
                      {ficha.precios_sueltos?.length > 1 && !ficha.precio_costo && (
                        <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.6)', marginTop:'7px' }}>
                          El mensaje trae estos precios: {ficha.precios_sueltos.map(v=>'$'+Number(v).toLocaleString('es-CO')).join(' · ')}
                        </div>
                      )}

                      {/* Escogido, no adivinado.
                          Los avisos de WhatsApp no traen categoría, y leerla del nombre no
                          funciona con estos nombres: «Lampara de uñas» cae en Iluminación
                          porque pega con «lámpara» antes de mirar «uñas», y un portátil o un
                          taladro se quedan en «Recién llegado». Sin escoger, el producto
                          terminaba en «Otros», que es donde nadie lo busca. */}
                      {/* El código del catálogo del mayorista. En los avisos de Metro va en la
                          pastilla de la esquina y es lo que hay que darle al pedirlo. */}
                      <div style={{ marginTop:'12px' }}>
                        <label style={s.label}>Código del proveedor</label>
                        <input style={s.input} value={ficha.codigo_proveedor || ''}
                          onChange={e=>cambiar({codigo_proveedor: e.target.value.toUpperCase()})}
                          placeholder="Ej: 01312 — el de la esquina del aviso" />
                        {ficha.codigo_proveedor && (
                          <div style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.45)', marginTop:'5px' }}>
                            Con esto el código del producto queda <b>{(avisoProveedor === 'El Paisa' ? 'EP' : avisoProveedor === 'Metro' ? 'MT' : avisoProveedor === 'IluminaLEDs' ? 'IL' : 'TP')}-{ficha.codigo_proveedor}</b>, igual que en el catálogo de {avisoProveedor}.
                          </div>
                        )}
                      </div>

                      <div style={{ marginTop:'12px' }}>
                        <label style={s.label}>¿En qué parte de la tienda va? *</label>
                        <select style={s.input} value={ficha.departamento || ''}
                          onChange={e=>cambiar({departamento: e.target.value, subcategoria_elegida: ''})}>
                          <option value="">— escoge el departamento —</option>
                          {deps.map(d => <option key={d.categoria} value={d.categoria}>{d.categoria} ({d.total})</option>)}
                        </select>
                        {(() => {
                          const subs = deps.find(d => d.categoria === ficha.departamento)?.subcategorias || [];
                          if (!ficha.departamento || !subs.length) return null;
                          return (
                            <select style={{...s.input, marginTop:'8px'}} value={ficha.subcategoria_elegida || ''}
                              onChange={e=>cambiar({subcategoria_elegida: e.target.value})}>
                              <option value="">— sin subcategoría —</option>
                              {subs.map(x => <option key={x} value={x}>{x}</option>)}
                            </select>
                          );
                        })()}
                        {!ficha.departamento && (
                          <div style={{ fontSize:'11.5px', color:'#FCD34D', marginTop:'6px' }}>
                            Sin esto el producto queda en «Otros», donde nadie lo busca.
                          </div>
                        )}
                      </div>

      {/* De donde salieron importa: lo del mensaje es exacto, lo de la imagen es
                          interpretacion y hay que mirarlo. Decir «del mensaje» cuando salieron
                          de la foto es mentirle a quien tiene que revisarlas. */}
                      {ficha.caracteristicas?.length > 0 && (
                        <div style={{ fontSize:'12px', color: ficha.caracteristicas_de_imagen ? '#FCD34D' : 'rgba(255,255,255,0.5)', marginTop:'10px', lineHeight:'1.6' }}>
                          {ficha.caracteristicas_de_imagen ? '⚠️ ' : ''}
                          <b style={{color: ficha.caracteristicas_de_imagen ? '#FCD34D' : 'rgba(255,255,255,0.7)'}}>{ficha.caracteristicas.length} características</b>
                          {ficha.caracteristicas_de_imagen ? ' leídas de la imagen — revísalas' : ' leídas del mensaje'}: {ficha.caracteristicas.slice(0,3).join(' · ')}{ficha.caracteristicas.length>3?'…':''}
                        </div>
                      )}

                      <div style={{ marginTop:'14px' }}>
                        <label style={s.label}>¿Ya lo tienes en el catálogo?</label>
                        {ficha.candidatos === null && <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.4)' }}>Buscando…</div>}
                        {ficha.candidatos?.length === 0 && <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.4)' }}>No encontré ninguno parecido. Se creará nuevo.</div>}
                        {/* Identificado por codigo: no es un parecido que haya que valorar. */}
                        {ficha.candidatos?.some(c => c.por_codigo) && (
                          <div style={{ background:'rgba(34,197,94,0.12)', color:'#86EFAC', padding:'9px 11px', borderRadius:'8px', fontSize:'12.5px', marginBottom:'8px', lineHeight:'1.5' }}>
                            ✅ <b>Ya lo tienes.</b> El código <b>{ficha.codigo_proveedor}</b> coincide con un producto tuyo de {avisoProveedor} — no es un parecido, es el mismo. Ya quedó escogido.
                          </div>
                        )}
                        <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'6px' }}>
                          {ficha.candidatos?.length > 0 && (
                            <button style={{ ...s.btn, ...(!ficha.elegido ? s.btnSel : s.btnS) }}
                              onClick={()=>cambiar({elegido:null})}>✨ Es nuevo</button>
                          )}
                          {(ficha.candidatos || []).map(c => {
                            // El del codigo va en verde cuando esta escogido: no es un
                            // parecido que haya que valorar, es el mismo producto.
                            const puesto = ficha.elegido === c.id;
                            const verde = { background:'rgba(34,197,94,0.15)', color:'#86EFAC', border:'1px solid rgba(34,197,94,0.5)' };
                            return (
                              <button key={c.id} title={c.porque}
                                style={{ ...s.btn, textAlign:'left', maxWidth:'100%',
                                         ...(puesto ? (c.por_codigo ? verde : s.btnSel) : s.btnS) }}
                                onClick={()=>cambiar({elegido: puesto ? null : c.id})}>
                                {c.por_codigo ? '✅ ' : ''}{c.nombre.slice(0,40)}{c.nombre.length>40?'…':''} · ${Number(c.precio_venta).toLocaleString('es-CO')}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {ficha.msg && <div style={{ background:'rgba(239,68,68,0.1)',color:'#FCA5A5',padding:'9px',borderRadius:'8px',marginTop:'10px',fontSize:'12.5px' }}>{ficha.msg}</div>}

                      <button style={{...s.btn,...s.btnP, width:'100%', marginTop:'12px', padding:'13px'}}
                        onClick={publicarAviso}
                        disabled={ficha.estado==='subiendo' || !ficha.precio_costo || !ficha.nombre
                                  || (!ficha.elegido && !ficha.departamento)}>
                        {ficha.estado==='subiendo' ? 'Subiendo…'
                          : ficha.elegido ? '🔄 Actualizar el que ya tengo y destacarlo'
                          : !ficha.departamento ? 'Escoge en qué parte de la tienda va'
                          : '➕ Crear y publicar'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {ficha?.estado === 'listo' && (
                <div style={{ textAlign:'center', marginTop:'14px' }}>
                  <a href="https://linkustelecom.com/novedades" target="_blank" rel="noopener"
                    style={{ color:'#00E5FF', fontSize:'13.5px', fontWeight:'600' }}>Ver la página de novedades →</a>
                </div>
              )}
            </div>
          </div>
        )}

        {modal && (
          <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center' }}>
            <div style={{ background:'#0F1629',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'14px',padding:'28px',width:'100%',maxWidth:'540px',maxHeight:'90vh',overflowY:'auto' }}>
              <h2 style={{ fontSize:'16px',fontWeight:'700',marginBottom:'20px',color:'#fff' }}>
                {editando ? 'Editar producto' : 'Nuevo producto'}
              </h2>
              {msg && <div style={{ background:'rgba(239,68,68,0.1)',color:'#FCA5A5',padding:'10px',borderRadius:'8px',marginBottom:'14px',fontSize:'13px' }}>{msg}</div>}
              <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
                <div><label style={s.label}>Nombre *</label><input style={s.input} value={form.nombre} onChange={e=>f('nombre',e.target.value)} placeholder="Como lo va a leer el cliente"/></div>
                <div style={s.grid2}>
                  <div>
                    <label style={s.label}>Proveedor</label>
                    <select style={s.input} value={form.proveedor} onChange={e=>{
                      f('proveedor', e.target.value);
                      /* Ya no se fuerza «Variedades»: esa pestaña desapareció cuando la tienda
                         pasó a organizarse por departamentos, y un producto nuevo de El Paisa
                         nacía en una estantería que no existe. El servidor lo acomoda solo. */
                    }}>
                      <option value="Tecnoplaza">Tecnoplaza</option>
                      <option value="Metro">Metro</option>
                      <option value="El Paisa">El Paisa</option>
                      <option value="IluminaLEDs">IluminaLEDs</option>
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>SKU *</label>
                    <div style={{ display:'flex', gap:'6px' }}>
                      <input style={s.input} value={form.sku} onChange={e=>f('sku',e.target.value)} placeholder="Ej: TL-WR840N"/>
                      {!editando && <button style={{...s.btn,...s.btnW,whiteSpace:'nowrap'}} onClick={sugerirSku} title="Arma un SKU a partir del nombre">Auto</button>}
                    </div>
                  </div>
                </div>

                {/* Categoría y subcategoría por lista, para TODOS los proveedores. Las opciones
                    salen del catálogo real de cada uno (`/categorias-de`), no de una lista
                    escrita aquí: cuando el proveedor agregue una categoría nueva en su próxima
                    importación, aparece sola. Antes esto era texto libre y escribir «Redes »
                    con un espacio creaba una categoría nueva en la tienda con un producto. */}
                <div style={s.grid2}>
                  <div>
                    <label style={s.label}>Categoría</label>
                    <select style={s.input} value={form.categoria}
                      onChange={e=>{ f('categoria', e.target.value); f('subcategoria',''); }}>
                      <option value="">— escoge una —</option>
                      {cats.map(c => <option key={c.categoria} value={c.categoria}>{c.categoria} ({c.total})</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>Subcategoría</label>
                    <select style={s.input} value={form.subcategoria}
                      onChange={e=>f('subcategoria',e.target.value)}
                      disabled={!subcatsDisponibles.length}>
                      <option value="">{subcatsDisponibles.length ? '— escoge una —' : 'esta categoría no usa subcategorías'}</option>
                      {subcatsDisponibles.map(x => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                </div>

                <div style={s.grid2}>
                  <div>
                    <label style={s.label}>Precio costo * (lo que te cobra el proveedor)</label>
                    <input style={s.input} type="number" value={form.precio_costo} onChange={e=>f('precio_costo',e.target.value)}/>
                  </div>
                  <div>
                    <label style={s.label}>Precio venta</label>
                    <input style={{...s.input,color:'#22C55E'}} type="number" value={form.precio_venta}
                      onChange={e=>{ setPrecioTocado(true); f('precio_venta',e.target.value); }}/>
                    {/* De dónde salió el precio. Un número que aparece solo, sin decir por qué,
                        no se puede revisar — y aquí hay dos escalas muy distintas en juego. */}
                    {sugerido && sugerido.escala && (
                      <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.45)', marginTop:'4px' }}>
                        escala {sugerido.escala}: +{sugerido.margen_pct}% → {fmt(sugerido.precio_venta)}
                        {precioTocado && +form.precio_venta !== sugerido.precio_venta && ' (lo cambiaste)'}
                      </div>
                    )}
                  </div>
                </div>

                {!editando && (
                  <div>
                    <label style={s.label}>Foto del proveedor — sin ella el producto NO sale en la tienda</label>
                    <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
                      <button style={{...s.btn,...s.btnW}} onClick={escogerFoto}>
                        {form.imagen_base64
                          ? `Cambiar fotos${form.fotos?.length ? ` (1 + ${form.fotos.length})` : ''}`
                          : '📷 Escoger fotos'}
                      </button>
                      {form.imagen_base64 && (
                        <button style={{...s.btn, background:'rgba(168,85,247,0.15)', border:'1px solid rgba(168,85,247,0.4)', color:'#C084FC'}}
                          onClick={leerLaFoto} disabled={leyendo}
                          title="Saca el nombre y las características del aviso que te mandaron">
                          {leyendo ? 'Leyendo…' : '✨ Leer la foto y llenar'}
                        </button>
                      )}
                      {form.imagen_base64
                        ? <img src={`data:image/jpeg;base64,${form.imagen_base64}`} alt="" style={{ width:'54px', height:'54px', objectFit:'contain', borderRadius:'8px', background:'rgba(255,255,255,0.06)' }}/>
                        : <span style={{ fontSize:'12px', color:'#FCA5A5' }}>Sin foto queda guardado pero oculto</span>}
                    </div>
                    {/* Se dice qué se leyó y qué NO: el precio no está en el aviso, y si no
                        se avisa, alguien va a dar por hecho que también salió de ahí. */}
                    {lectura && (
                      <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)', marginTop:'6px', lineHeight:'1.5' }}>
                        Leído del aviso{lectura.marca ? ` · marca ${lectura.marca}` : ''}
                        {lectura.referencia ? ` · ref ${lectura.referencia}` : ''}
                        {lectura.categoria ? ` · ${lectura.categoria}` : ''}.
                        <b style={{color:'#FDBA74'}}> Revísalo y escribe tú el precio</b> — ese no viene en la imagen.
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label style={s.label}>Descripción — opcional</label>
                  <textarea style={{...s.input, minHeight:'60px', resize:'vertical'}} value={form.descripcion}
                    onChange={e=>f('descripcion',e.target.value)} placeholder="Lo que el proveedor mandó del producto"/>
                </div>

                <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', color:'rgba(255,255,255,0.8)' }}>
                  <input type="checkbox" checked={form.en_tienda !== false} onChange={e=>f('en_tienda', e.target.checked)}/>
                  Publicarlo en la tienda web
                </label>

                <div>
                  <label style={s.label}>💥 Precio ANTES (oferta) — opcional</label>
                  <input style={s.input} type="number" value={form.precio_antes} onChange={e=>f('precio_antes',e.target.value)} placeholder="Si lo llenas, la tienda muestra % OFF" />
                </div>
              </div>
              <div style={{ display:'flex', gap:'10px', marginTop:'20px', justifyContent:'flex-end' }}>
                <button style={{...s.btn,...s.btnW}} onClick={()=>setModal(false)}>Cancelar</button>
                <button style={{...s.btn,...s.btnP}} onClick={guardar}>Guardar</button>
              </div>
            </div>
          </div>
        )}

        {/* El mensaje de WhatsApp, para revisarlo antes de mandarlo */}
        {waTexto && (
          <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:600,display:'flex',alignItems:'center',justifyContent:'center' }}>
            <div style={{ background:'#0F1629',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'14px',padding:'24px',width:'100%',maxWidth:'520px' }}>
              <h2 style={{ fontSize:'16px',fontWeight:'700',marginBottom:'6px',color:'#fff' }}>💬 Mensaje para WhatsApp</h2>
              <p style={{ fontSize:'12px', color:'rgba(255,255,255,0.45)', margin:'0 0 14px' }}>
                Ya quedó copiado. Al pegar el enlace, WhatsApp muestra solo la foto y el precio.
              </p>
              <textarea readOnly value={waTexto.texto}
                style={{...s.input, minHeight:'190px', fontFamily:'inherit', lineHeight:'1.5', resize:'vertical'}}
                onFocus={e=>e.target.select()}/>
              <div style={{ display:'flex', gap:'10px', marginTop:'16px', justifyContent:'flex-end', flexWrap:'wrap' }}>
                <button style={{...s.btn,...s.btnW}} onClick={()=>setWaTexto(null)}>Cerrar</button>
                <button style={{...s.btn,...s.btnW}} onClick={()=>navigator.clipboard.writeText(waTexto.texto).then(()=>alert('Copiado'), ()=>alert('Selecciónalo y cópialo a mano'))}>Copiar otra vez</button>
                <a href={waTexto.enlace} target="_blank" rel="noreferrer"
                   style={{...s.btn, background:'#25D366', color:'#0B141A', textDecoration:'none', fontWeight:'700'}}>Abrir WhatsApp</a>
              </div>
            </div>
          </div>
        )}

        {/* Modal importar PDF */}
        {modalImport && (
          <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center' }}>
            <div style={{ background:'#0F1629',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'14px',padding:'28px',width:'100%',maxWidth:'680px',maxHeight:'90vh',overflowY:'auto' }}>
              <h2 style={{ fontSize:'16px',fontWeight:'700',marginBottom:'8px',color:'#fff' }}>
                📋 Actualizar catálogo — <span style={{color:PROV_COLOR[proveedorImport]}}>{proveedorImport}</span>
              </h2>
              <p style={{ fontSize:'13px',color:'rgba(255,255,255,0.4)',marginBottom:'12px' }}>
                Sube el PDF que te envía {proveedorImport}. El sistema detecta los productos,
                actualiza los precios y agrega los nuevos.
              </p>
              <div style={{ background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'8px', padding:'11px 13px', marginBottom:'16px', fontSize:'12.5px', color:'#86EFAC', lineHeight:'1.5' }}>
                🛡️ <b>Antes de importar se guarda un respaldo automático.</b>
                {proveedorImport === 'Tecnoplaza' ? (
                  <> La lista de Tecnoplaza <b>solo sirve para saber el costo real</b> de cada producto:
                  no crea, no borra ni oculta nada. Los precios de venta siguen saliendo de su página web.
                  Después de importar puedes revisar los cruces en <b>🔍 Revisar costos del PDF</b>.</>
                ) : (
                  <> No se borra nada: los productos que ya existen solo cambian de precio y conservan
                  sus fotos, fichas técnicas y categorías. Los que ya no aparezcan en el PDF quedan
                  marcados como agotados, sin perder su información.</>
                )}
              </div>
              {msgImport && (
                <div style={{ background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.2)',color:'#86EFAC',padding:'12px',borderRadius:'8px',marginBottom:'14px',fontSize:'13px' }}>
                  {msgImport}
                </div>
              )}
              <div>
                <label style={s.label}>Archivo PDF de {proveedorImport}</label>
                <div style={{ border:'2px dashed rgba(255,255,255,0.15)', borderRadius:'10px', padding:'28px', textAlign:'center', background:'rgba(255,255,255,0.02)' }}>
                  <input type="file" accept="application/pdf" id="pdf-upload"
                    style={{ display:'none' }}
                    onChange={e=>setArchivoPDF(e.target.files[0])}/>
                  <label htmlFor="pdf-upload" style={{ cursor:'pointer', display:'block' }}>
                    <div style={{ fontSize:'32px', marginBottom:'8px' }}>📄</div>
                    {archivoPDF ? (
                      <div style={{ color:'#22C55E', fontWeight:'600', fontSize:'13px' }}>{archivoPDF.name}</div>
                    ) : (
                      <div style={{ color:'rgba(255,255,255,0.5)', fontSize:'13px' }}>Haz clic para seleccionar el PDF de {proveedorImport}</div>
                    )}
                  </label>
                </div>
              </div>
              <div style={{ display:'flex', gap:'10px', marginTop:'16px', justifyContent:'flex-end' }}>
                <button style={{...s.btn,...s.btnW}} onClick={()=>{setModalImport(false);setArchivoPDF(null);}}>Cerrar</button>
                <button style={{...s.btn,...(proveedorImport==='Metro'?s.btnPink:s.btnG)}} onClick={importarPDF} disabled={importando || !archivoPDF}>
                  {importando ? '⏳ Procesando PDF...' : `🚀 Importar ${proveedorImport}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
